import React, { useMemo, useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

function perfColor(score) {
  if (!score) return "rgba(255,255,255,0.2)";
  if (score >= 4.5) return "#00ff90";
  if (score >= 3.5) return "#60a5fa";
  if (score >= 2.5) return "#fbbf24";
  return "#f87171";
}

export default function TeamWorkloadPanel({ members = [], tasks = [], projects = [], compact }) {
  const [perfData, setPerfData] = useState({});

  useEffect(() => {
    const tok = localStorage.getItem("token");
    fetch(`${API}/api/projects/members/performance`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.json())
      .then(d => { if (d && typeof d === "object") setPerfData(d); })
      .catch(() => {});
  }, []);

  const memberStats = useMemo(() => members.map(m => {
    const myTasks   = tasks.filter(t => t.assignee_id === m.id);
    const done      = myTasks.filter(t => t.status === "Done").length;
    const inProg    = myTasks.filter(t => t.status === "In Progress").length;
    const overdue   = myTasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== "Done").length;
    const completion= myTasks.length ? Math.round((done / myTasks.length) * 100) : 0;
    const perf      = perfData[m.id] || m.perf_rating;
    return { ...m, myTasks, done, inProg, overdue, completion, perf };
  }), [members, tasks, perfData]);

  const chartData = memberStats.map(m => ({
    name: m.name?.split(" ")[0] || m.name,
    tasks: m.myTasks.length,
    done:  m.done,
  }));

  if (compact) {
    return (
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Team Workload</span>
        </div>
        <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
          {memberStats.map(m => (
            <div key={m.id} className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/3 px-3 py-2">
              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-xs font-medium text-white/50 shrink-0">
                {m.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white/70 font-medium truncate">{m.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex-1 h-1 rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-[#00ff90]" style={{ width: `${m.completion}%` }} />
                  </div>
                  <span className="text-xs text-white/30">{m.completion}%</span>
                </div>
              </div>
              <span className="text-xs text-white/30">{m.myTasks.length}t</span>
              {m.perf && (
                <span className="text-xs font-bold" style={{ color: perfColor(m.perf) }}>{parseFloat(m.perf).toFixed(1)}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Chart */}
      {chartData.length > 0 && (
        <div className={`${card} p-5`}>
          <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Tasks per Member</p>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.3)" }} />
              <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="tasks" name="Total"    radius={[4,4,0,0]} fill="#60a5fa" fillOpacity={0.5} />
              <Bar dataKey="done"  name="Completed" radius={[4,4,0,0]} fill="#00ff90" fillOpacity={0.8} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Member cards */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Team Performance & Workload</span>
        </div>
        <div className="divide-y divide-white/5">
          {memberStats.length === 0 ? (
            <p className="px-5 py-6 text-white/20 text-sm italic text-center">No team members.</p>
          ) : memberStats.map(m => (
            <div key={m.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-sm font-bold text-white/50 shrink-0">
                    {m.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-white/80 font-medium">{m.name}</p>
                    <p className="text-xs text-white/30">{m.role || m.dept || "—"}</p>
                  </div>
                </div>

                {/* Perf score */}
                {m.perf && (
                  <div className="text-center shrink-0">
                    <p className="text-xl font-bold" style={{ color: perfColor(m.perf) }}>
                      {parseFloat(m.perf).toFixed(1)}
                    </p>
                    <p className="text-xs text-white/25">Perf Score</p>
                  </div>
                )}
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 gap-3 mt-4">
                {[
                  { label: "Total",       value: m.myTasks.length, color: "#ffffff60" },
                  { label: "In Progress", value: m.inProg,          color: "#fbbf24"  },
                  { label: "Completed",   value: m.done,            color: "#00ff90"  },
                  { label: "Overdue",     value: m.overdue,         color: m.overdue > 0 ? "#f87171" : "#ffffff30" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-lg border border-white/5 bg-white/3 p-2 text-center">
                    <p className="text-base font-bold" style={{ color }}>{value}</p>
                    <p className="text-xs text-white/25">{label}</p>
                  </div>
                ))}
              </div>

              {/* Completion bar */}
              <div className="flex items-center gap-3 mt-3">
                <div className="flex-1 h-2 rounded-full bg-white/5">
                  <div className="h-full rounded-full bg-[#00ff90] transition-all" style={{ width: `${m.completion}%` }} />
                </div>
                <span className="text-xs font-medium text-[#00ff90]/60 w-10 text-right">{m.completion}%</span>
              </div>

              {/* Recent tasks */}
              {m.myTasks.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {m.myTasks.slice(0, 4).map(t => (
                    <span key={t.id} className={`text-xs px-2 py-0.5 rounded-full border ${
                      t.status === "Done" ? "text-[#00ff90]/50 bg-[#00ff90]/5 border-[#00ff90]/15" :
                      t.status === "In Progress" ? "text-yellow-400/50 bg-yellow-500/5 border-yellow-500/15" :
                      "text-white/25 bg-white/3 border-white/8"
                    }`}>
                      {t.title?.slice(0, 22)}{t.title?.length > 22 ? "…" : ""}
                    </span>
                  ))}
                  {m.myTasks.length > 4 && <span className="text-xs text-white/20">+{m.myTasks.length - 4} more</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}